import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
    Snowflake, Play, ArrowUpRight, Heart, Ruler, Trash2, Save, Plus,
    Clock, AlertCircle, CalendarClock,
} from "lucide-react";

const money = (n) => "\u20B9" + (n || 0).toLocaleString("en-IN");
const Card = ({ children, className = "" }) =>
    <div className={`card-noir p-6 lg:p-8 ${className}`}>{children}</div>;
const todayStr = () => new Date().toISOString().slice(0, 10);

const DURATION_PRESETS = [
    { key: "weekly", label: "Weekly", days: 7 },
    { key: "monthly", label: "Monthly", days: 30 },
    { key: "six_month", label: "6 Months", days: 180 },
    { key: "yearly", label: "Yearly", days: 365 },
    { key: "custom", label: "Custom days", days: null },
];

// =========================================================================
// PLANS TAB (admin CRUD, staff read-only)
// =========================================================================
export const PlansTab = ({ isAdmin }) => {
    const [rows, setRows] = useState([]);
    const [emps, setEmps] = useState([]);
    const [editing, setEditing] = useState(null); // plan id being edited
    const blank = { name: "", description: "", duration_kind: "monthly",
                    duration_days: 30, price: 0, gst: 0, trainer_id: "", active: true };
    const [f, setF] = useState(blank);
    const load = async () => {
        const [p, e] = await Promise.all([
            api.get("/admin/plans"),
            isAdmin ? api.get("/admin/employees") : Promise.resolve({ data: [] }),
        ]);
        setRows(p.data); setEmps(e.data);
    };
    useEffect(() => { load(); }, []);
    const onKind = (k) => {
        const pr = DURATION_PRESETS.find((x) => x.key === k);
        setF({ ...f, duration_kind: k, duration_days: pr?.days ?? f.duration_days });
    };
    const save = async () => {
        if (!f.name || !f.duration_days || f.price < 0) return toast.error("Name, duration and price are required");
        try {
            if (editing) {
                await api.patch(`/admin/plans/${editing}`, f);
                toast.success("Plan updated");
            } else {
                await api.post("/admin/plans", f);
                toast.success("Plan created");
            }
            setF(blank); setEditing(null); load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };
    const edit = (p) => { setEditing(p.id); setF({ ...blank, ...p, trainer_id: p.trainer_id || "" }); };
    const del = async (id) => {
        if (!window.confirm("Deactivate this plan? Historical subscriptions remain unchanged.")) return;
        await api.delete(`/admin/plans/${id}`); toast.success("Plan deactivated"); load();
    };
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            {isAdmin && (
                <Card>
                    <div className="font-mono-label">{editing ? "Edit plan" : "Add plan"}</div>
                    <div className="space-y-3 mt-4">
                        <Input placeholder="Plan name (e.g. Eve Gold Monthly)" value={f.name}
                               onChange={(e) => setF({ ...f, name: e.target.value })}
                               data-testid="plan-name" />
                        <Textarea placeholder="Description (visible to staff)" rows={2}
                                  value={f.description || ""}
                                  onChange={(e) => setF({ ...f, description: e.target.value })}
                                  data-testid="plan-desc" />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-sand/70">Duration</Label>
                                <Select value={f.duration_kind} onValueChange={onKind}>
                                    <SelectTrigger data-testid="plan-duration-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {DURATION_PRESETS.map((p) =>
                                            <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-sand/70">Days</Label>
                                <Input type="number" min="1" value={f.duration_days}
                                       onChange={(e) => setF({ ...f, duration_days: parseInt(e.target.value || "0", 10) })}
                                       disabled={f.duration_kind !== "custom"}
                                       data-testid="plan-duration-days" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-sand/70">Price (\u20B9)</Label>
                                <Input type="number" min="0" value={f.price}
                                       onChange={(e) => setF({ ...f, price: parseFloat(e.target.value || "0") })}
                                       data-testid="plan-price" />
                            </div>
                            <div>
                                <Label className="text-xs text-sand/70">GST (\u20B9)</Label>
                                <Input type="number" min="0" value={f.gst}
                                       onChange={(e) => setF({ ...f, gst: parseFloat(e.target.value || "0") })} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-sand/70">Assigned trainer</Label>
                            <Select value={f.trainer_id || "__none__"}
                                    onValueChange={(v) => setF({ ...f, trainer_id: v === "__none__" ? "" : v })}>
                                <SelectTrigger data-testid="plan-trainer"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— None —</SelectItem>
                                    {emps.filter((e) => e.active !== false).map((e) =>
                                        <SelectItem key={e.id} value={e.id}>{e.name} \u00b7 {e.role}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={save} className="btn-gold w-full" data-testid="plan-save">
                            <Save className="w-4 h-4 mr-1" /> {editing ? "Save changes" : "Create plan"}
                        </Button>
                        {editing && <Button variant="ghost" onClick={() => { setEditing(null); setF(blank); }}
                                            className="w-full text-sand">Cancel</Button>}
                    </div>
                </Card>
            )}
            <Card className={isAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
                <div className="font-mono-label">Membership plans</div>
                <Table className="mt-3">
                    <TableHeader><TableRow>
                        <TableHead>Name</TableHead><TableHead>Duration</TableHead>
                        <TableHead>Price</TableHead><TableHead>Trainer</TableHead>
                        <TableHead>Active</TableHead>{isAdmin && <TableHead></TableHead>}
                    </TableRow></TableHeader>
                    <TableBody>
                        {rows.map((p) => (
                            <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                                <TableCell className="text-ivory">{p.name}</TableCell>
                                <TableCell className="text-sand">{p.duration_kind} \u00b7 {p.duration_days}d</TableCell>
                                <TableCell className="text-gold">{money(p.price)}</TableCell>
                                <TableCell className="text-sand text-sm">{p.trainer_name || "—"}</TableCell>
                                <TableCell className={p.active ? "text-emerald-400" : "text-sand/60"}>
                                    {p.active ? "Yes" : "No"}
                                </TableCell>
                                {isAdmin && (
                                    <TableCell className="flex gap-3">
                                        <button onClick={() => edit(p)} className="text-gold text-xs hover:underline"
                                                data-testid={`plan-edit-${p.id}`}>Edit</button>
                                        <button onClick={() => del(p.id)} className="text-crimson text-xs"
                                                data-testid={`plan-del-${p.id}`}><Trash2 className="inline w-3 h-3" /></button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {!rows.length && <TableRow><TableCell colSpan={isAdmin ? 6 : 5}
                            className="text-sand/70 text-center py-6">No plans yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
};

// =========================================================================
// TRAINER ROSTER TAB
// =========================================================================
export const TrainerRosterTab = () => {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get("/admin/trainers/roster").then(({ data }) => setRows(data)).catch(() => setRows([]));
    }, []);
    return (
        <div className="space-y-4">
            <div className="font-mono-label"><Ruler className="inline w-4 h-4 mr-1" /> Trainer roster &middot; active assignments</div>
            {rows.length === 0 && <Card><div className="text-sand/70 text-center py-6">No trainers or subscriptions yet.</div></Card>}
            {rows.map((t) => (
                <Card key={t.trainer_id} className="!p-6">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="font-display text-xl text-ivory">{t.trainer_name}</div>
                            <div className="text-xs text-sand/70">{t.role} &middot; {t.shift || "—"} &middot; <a href={`tel:${t.phone}`} className="text-gold hover:underline">{t.phone}</a></div>
                        </div>
                        <div className="text-gold text-sm font-mono-label">{t.member_count} member{t.member_count === 1 ? "" : "s"}</div>
                    </div>
                    {t.members.length > 0 && (
                        <Table className="mt-3">
                            <TableHeader><TableRow>
                                <TableHead>Member</TableHead><TableHead>Plan</TableHead>
                                <TableHead>Ends</TableHead><TableHead>Status</TableHead>
                                <TableHead>Contact</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                                {t.members.map((m) => (
                                    <TableRow key={m.subscription_id} data-testid={`roster-row-${m.subscription_id}`}>
                                        <TableCell className="text-ivory">{m.member_name}</TableCell>
                                        <TableCell className="text-sand">{m.plan_name}</TableCell>
                                        <TableCell className="text-sand">{m.end_date}</TableCell>
                                        <TableCell className={m.status === "active" ? "text-emerald-400" : "text-blue-400"}>{m.status}</TableCell>
                                        <TableCell className="flex gap-3 text-xs">
                                            {m.phone && <a href={`tel:${m.phone}`} className="text-gold hover:underline">Call</a>}
                                            {m.phone && <a href={`https://wa.me/${m.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">WhatsApp</a>}
                                            {m.email && <a href={`mailto:${m.email}`} className="text-sand hover:underline">Email</a>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Card>
            ))}
        </div>
    );
};

export const FollowupsTab = () => {
    const [rows, setRows] = useState([]);
    const [horizon, setHorizon] = useState(14);
    const load = async () => {
        const { data } = await api.get("/admin/subscriptions/followups", { params: { days_ahead: horizon } });
        setRows(data);
    };
    useEffect(() => { load(); }, [horizon]);
    const badge = (r) => r.is_expired ? "text-crimson" : r.days_left <= 3 ? "text-gold" : "text-sand";
    return (
        <Card>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="font-mono-label"><CalendarClock className="inline w-4 h-4 mr-1" />
                    Membership follow-ups</div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-sand/70">Window</span>
                    <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v, 10))}>
                        <SelectTrigger className="w-32" data-testid="followup-horizon"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {[7, 14, 30, 60].map((d) => <SelectItem key={d} value={String(d)}>Next {d} days</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="text-xs text-sand/70 mt-2">
                {rows.length} to action \u00b7 expired ones show in red, expiring within 3 days in gold.
            </div>
            <Table className="mt-4">
                <TableHeader><TableRow>
                    <TableHead>Member</TableHead><TableHead>Plan</TableHead>
                    <TableHead>Trainer</TableHead><TableHead>End date</TableHead>
                    <TableHead>Days left</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.id} data-testid={`followup-row-${r.id}`}>
                            <TableCell className="text-ivory">{r.member_name}</TableCell>
                            <TableCell className="text-sand">{r.plan_name}</TableCell>
                            <TableCell className="text-sand text-sm">{r.trainer_name || "—"}</TableCell>
                            <TableCell className="text-sand">{r.end_date}</TableCell>
                            <TableCell className={badge(r)}>
                                {r.is_expired ? <><AlertCircle className="inline w-3 h-3 mr-1" />{Math.abs(r.days_left)}d overdue</>
                                             : <><Clock className="inline w-3 h-3 mr-1" />{r.days_left}d</>}
                            </TableCell>
                            <TableCell className="text-xs">{r.status}</TableCell>
                        </TableRow>
                    ))}
                    {!rows.length && <TableRow><TableCell colSpan={6}
                        className="text-sand/70 text-center py-6">Nothing to follow up on. \u2728</TableCell></TableRow>}
                </TableBody>
            </Table>
        </Card>
    );
};

// =========================================================================
// MEMBER MANAGER — Dialog with Subscription / Health / Measurements
// =========================================================================
export const MemberManagerModal = ({ member, open, onClose, isAdmin, onMemberUpdate }) => {
    if (!member) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-obsidian-light border border-gold/25 text-ivory max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl gold-text">{member.name}</DialogTitle>
                    <div className="text-xs text-sand/70">{member.phone} \u00b7 {member.email || "no email"}</div>
                </DialogHeader>
                <Tabs defaultValue="profile" className="mt-2">
                    <TabsList className="bg-obsidian border border-gold/20">
                        <TabsTrigger value="profile" data-testid="mm-tab-profile">Profile</TabsTrigger>
                        <TabsTrigger value="subscription" data-testid="mm-tab-sub">Subscription</TabsTrigger>
                        <TabsTrigger value="health" data-testid="mm-tab-health">Health</TabsTrigger>
                        <TabsTrigger value="measurements" data-testid="mm-tab-meas">Measurements</TabsTrigger>
                    </TabsList>
                    <TabsContent value="profile">
                        <ProfilePanel member={member} isAdmin={isAdmin} onMemberUpdate={onMemberUpdate} onClose={onClose} />
                    </TabsContent>
                    <TabsContent value="subscription">
                        <SubscriptionPanel member={member} isAdmin={isAdmin} />
                    </TabsContent>
                    <TabsContent value="health"><HealthPanel member={member} /></TabsContent>
                    <TabsContent value="measurements"><MeasurementsPanel member={member} /></TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};


// ---- Profile sub-panel: edit name / phone / email / plan / biometric_id ----
const ProfilePanel = ({ member, isAdmin, onMemberUpdate, onClose }) => {
    const [f, setF] = useState({
        name: member.name || "",
        phone: member.phone || "",
        email: member.email || "",
        plan: member.plan || "Eve Gold",
        biometric_id: member.biometric_id || "",
        active: !!member.active,
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        setF({
            name: member.name || "", phone: member.phone || "", email: member.email || "",
            plan: member.plan || "Eve Gold", biometric_id: member.biometric_id || "",
            active: !!member.active,
        });
    }, [member.id]);
    const dirty = f.name !== member.name || f.phone !== member.phone ||
                  f.email !== (member.email || "") || f.plan !== member.plan ||
                  f.biometric_id !== (member.biometric_id || "") ||
                  f.active !== !!member.active;
    const save = async () => {
        if (!f.name.trim()) return toast.error("Name is required");
        if (!f.phone.trim()) return toast.error("Phone is required");
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/members/${member.id}`, f);
            toast.success("Profile saved");
            onMemberUpdate?.(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };
    return (
        <div className="space-y-4 py-2" data-testid="mm-profile-panel">
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs text-sand/70">Full name</Label>
                    <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                           data-testid="mm-profile-name" />
                </div>
                <div>
                    <Label className="text-xs text-sand/70">Phone</Label>
                    <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                           placeholder="+919..." data-testid="mm-profile-phone" />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-xs text-sand/70">Email</Label>
                    <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                           placeholder="member@example.com" data-testid="mm-profile-email" />
                </div>
                <div>
                    <Label className="text-xs text-sand/70">Plan tier</Label>
                    <Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}>
                        <SelectTrigger data-testid="mm-profile-plan"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["Eve Gold", "Eve Diamond", "Eve Empress VIP"].map((p) =>
                                <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs text-sand/70">Biometric ID (ESSL enrollment)</Label>
                    <Input value={f.biometric_id} onChange={(e) => setF({ ...f, biometric_id: e.target.value })}
                           placeholder="e.g. 1504023" data-testid="mm-profile-biometric" />
                </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-sand">
                <input type="checkbox" checked={f.active}
                       onChange={(e) => setF({ ...f, active: e.target.checked })}
                       data-testid="mm-profile-active" />
                Active member (unchecking blocks biometric entry)
            </label>
            <div className="flex items-center justify-between pt-2 border-t border-gold/15">
                <div className="text-[11px] text-sand/60">
                    {dirty ? "Unsaved changes" : "No changes"}
                </div>
                <div className="flex gap-2">
                    <Button variant="ghost" onClick={onClose} className="text-sand text-xs" data-testid="mm-profile-cancel">
                        Close
                    </Button>
                    <Button onClick={save} disabled={!dirty || busy} className="btn-gold text-xs"
                            data-testid="mm-profile-save">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ---- Subscription sub-panel ----
const SubscriptionPanel = ({ member, isAdmin }) => {
    const [subs, setSubs] = useState([]);
    const [plans, setPlans] = useState([]);
    const [planId, setPlanId] = useState("");
    const [upgradePlanId, setUpgradePlanId] = useState("");
    const load = async () => {
        const [s, p] = await Promise.all([
            api.get(`/admin/members/${member.id}/subscriptions`),
            api.get("/admin/plans"),
        ]);
        setSubs(s.data); setPlans(p.data.filter((x) => x.active));
    };
    useEffect(() => { load(); }, [member.id]);
    const active = subs.find((s) => s.status === "active" || s.status === "frozen");
    const subscribe = async () => {
        if (!planId) return toast.error("Choose a plan");
        try { await api.post(`/admin/members/${member.id}/subscribe`, { plan_id: planId });
              toast.success("Subscribed"); setPlanId(""); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Subscribe failed"); }
    };
    const freeze = async () => {
        const reason = window.prompt("Reason for freeze (optional)") || "";
        try { await api.post(`/admin/subscriptions/${active.id}/freeze`, { reason });
              toast.success("Frozen"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Freeze failed"); }
    };
    const unfreeze = async () => {
        try { const { data } = await api.post(`/admin/subscriptions/${active.id}/unfreeze`);
              toast.success(`Unfrozen \u00b7 end date extended by ${data.extended_days} days`); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Unfreeze failed"); }
    };
    const upgrade = async () => {
        if (!upgradePlanId) return toast.error("Choose an upgrade plan");
        try { await api.post(`/admin/subscriptions/${active.id}/upgrade`, { plan_id: upgradePlanId });
              toast.success("Upgraded"); setUpgradePlanId(""); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Upgrade failed"); }
    };
    return (
        <div className="space-y-4 py-2">
            {active ? (
                <div className={`rounded-xl border p-4 ${active.status === "frozen"
                    ? "border-blue-400/40 bg-blue-950/10" : "border-gold/40 bg-gold/5"}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-mono-label text-gold">{active.status.toUpperCase()}</div>
                            <div className="font-display text-lg text-ivory mt-1">{active.plan_name}</div>
                            <div className="text-xs text-sand/70 mt-1">
                                Trainer: {active.trainer_name || "—"} \u00b7
                                {" "}{active.start_date} \u2192 {active.end_date}
                                {active.freeze_days_used ? ` \u00b7 ${active.freeze_days_used}d frozen so far` : ""}
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                            {active.status === "active" && (
                                <Button variant="ghost" onClick={freeze}
                                        className="btn-ghost-gold text-xs"
                                        data-testid={`sub-freeze-${active.id}`}>
                                    <Snowflake className="w-3 h-3 mr-1" /> Freeze
                                </Button>
                            )}
                            {active.status === "frozen" && (
                                <Button onClick={unfreeze} className="btn-gold text-xs"
                                        data-testid={`sub-unfreeze-${active.id}`}>
                                    <Play className="w-3 h-3 mr-1" /> Unfreeze
                                </Button>
                            )}
                        </div>
                    </div>
                    {active.status === "active" && (
                        <div className="mt-3 flex items-center gap-2">
                            <Select value={upgradePlanId} onValueChange={setUpgradePlanId}>
                                <SelectTrigger className="flex-1" data-testid="sub-upgrade-select">
                                    <SelectValue placeholder="Upgrade to..." /></SelectTrigger>
                                <SelectContent>
                                    {plans.filter((p) => p.id !== active.plan_id).map((p) =>
                                        <SelectItem key={p.id} value={p.id}>{p.name} \u00b7 {money(p.price)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button onClick={upgrade} className="btn-gold text-xs"
                                    data-testid="sub-upgrade-btn">
                                <ArrowUpRight className="w-3 h-3 mr-1" /> Upgrade
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="rounded-xl border border-sand/20 p-4 space-y-3">
                    <div className="font-mono-label">No active subscription</div>
                    <div className="flex gap-2">
                        <Select value={planId} onValueChange={setPlanId}>
                            <SelectTrigger className="flex-1" data-testid="sub-plan-select">
                                <SelectValue placeholder="Choose plan" /></SelectTrigger>
                            <SelectContent>
                                {plans.map((p) =>
                                    <SelectItem key={p.id} value={p.id}>{p.name} \u00b7 {money(p.price)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button onClick={subscribe} className="btn-gold text-xs"
                                data-testid="sub-subscribe-btn">
                            <Plus className="w-3 h-3 mr-1" /> Subscribe
                        </Button>
                    </div>
                </div>
            )}
            {subs.length > 0 && (
                <div>
                    <div className="font-mono-label mb-2">History</div>
                    <Table>
                        <TableHeader><TableRow>
                            <TableHead>Plan</TableHead><TableHead>Range</TableHead>
                            <TableHead>Frozen d</TableHead><TableHead>Status</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {subs.map((s) => (
                                <TableRow key={s.id}>
                                    <TableCell className="text-sand">{s.plan_name}</TableCell>
                                    <TableCell className="text-xs text-sand/70">{s.start_date} \u2192 {s.end_date}</TableCell>
                                    <TableCell className="text-xs text-sand">{s.freeze_days_used || 0}</TableCell>
                                    <TableCell className={s.status === "active" ? "text-emerald-400"
                                        : s.status === "frozen" ? "text-blue-400"
                                        : s.status === "upgraded" ? "text-gold" : "text-sand/70"}>{s.status}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

// ---- Health sub-panel ----
const HealthPanel = ({ member }) => {
    const [f, setF] = useState({ illnesses: "", medications: "", surgeries: "",
                                  allergies: "", health_risks: "", notes: "" });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get(`/admin/members/${member.id}/health`).then(({ data }) => {
            if (data && data.member_id) setF({
                illnesses: data.illnesses || "", medications: data.medications || "",
                surgeries: data.surgeries || "", allergies: data.allergies || "",
                health_risks: data.health_risks || "", notes: data.notes || "",
            });
        });
    }, [member.id]);
    const save = async () => {
        setBusy(true);
        try { await api.post(`/admin/members/${member.id}/health`, f); toast.success("Saved"); }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
        setBusy(false);
    };
    return (
        <div className="space-y-3 py-2">
            <div><Label className="text-xs text-sand/70">Illnesses (history)</Label>
                <Textarea rows={2} value={f.illnesses}
                          onChange={(e) => setF({ ...f, illnesses: e.target.value })}
                          placeholder="e.g. asthma since 2018, mild hypertension"
                          data-testid="health-illnesses" /></div>
            <div><Label className="text-xs text-sand/70">Current medications</Label>
                <Textarea rows={2} value={f.medications}
                          onChange={(e) => setF({ ...f, medications: e.target.value })}
                          placeholder="e.g. inhaler Foracort BD"
                          data-testid="health-medications" /></div>
            <div><Label className="text-xs text-sand/70">Surgeries</Label>
                <Textarea rows={2} value={f.surgeries}
                          onChange={(e) => setF({ ...f, surgeries: e.target.value })}
                          placeholder="e.g. appendectomy 2019"
                          data-testid="health-surgeries" /></div>
            <div><Label className="text-xs text-sand/70">Allergies</Label>
                <Textarea rows={2} value={f.allergies}
                          onChange={(e) => setF({ ...f, allergies: e.target.value })}
                          data-testid="health-allergies" /></div>
            <div><Label className="text-xs text-sand/70">Health risks</Label>
                <Textarea rows={2} value={f.health_risks}
                          onChange={(e) => setF({ ...f, health_risks: e.target.value })}
                          placeholder="e.g. family history diabetes, PCOS"
                          data-testid="health-risks" /></div>
            <div><Label className="text-xs text-sand/70">Coach notes</Label>
                <Textarea rows={2} value={f.notes}
                          onChange={(e) => setF({ ...f, notes: e.target.value })}
                          data-testid="health-notes" /></div>
            <Button onClick={save} disabled={busy} className="btn-gold w-full"
                    data-testid="health-save">
                <Save className="w-4 h-4 mr-1" /> Save health profile
            </Button>
        </div>
    );
};

// ---- Measurements sub-panel ----
const MEAS_FIELDS = [
    ["weight", "Weight"], ["height", "Height"], ["neck", "Neck"], ["chest", "Chest"],
    ["waist", "Waist"], ["hips", "Hips"], ["bicep_left", "Bicep L"], ["bicep_right", "Bicep R"],
    ["thigh_left", "Thigh L"], ["thigh_right", "Thigh R"], ["calf_left", "Calf L"], ["calf_right", "Calf R"],
];
const MeasurementsPanel = ({ member }) => {
    const [rows, setRows] = useState([]);
    const blank = { date: todayStr(), unit_length: "cm", unit_weight: "kg", notes: "" };
    const [f, setF] = useState(blank);
    const load = async () => setRows((await api.get(`/admin/members/${member.id}/measurements`)).data);
    useEffect(() => { load(); }, [member.id]);
    const save = async () => {
        try { await api.post(`/admin/members/${member.id}/measurements`, f);
              toast.success("Logged"); setF(blank); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const del = async (id) => {
        await api.delete(`/admin/members/${member.id}/measurements/${id}`); load();
    };
    const delta = (key, i) => {
        // difference from previous chronological log for progress badge
        if (i >= rows.length - 1) return null;
        const cur = rows[i]?.[key], prev = rows[i + 1]?.[key];
        if (cur == null || prev == null) return null;
        const d = Math.round((cur - prev) * 10) / 10;
        if (d === 0) return null;
        return <span className={d < 0 ? "text-emerald-400" : "text-crimson"}> ({d > 0 ? "+" : ""}{d})</span>;
    };
    return (
        <div className="space-y-4 py-2">
            <div className="rounded-xl border border-gold/25 p-4">
                <div className="font-mono-label mb-3">New log</div>
                <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs text-sand/70">Date</Label>
                        <Input type="date" value={f.date}
                               onChange={(e) => setF({ ...f, date: e.target.value })}
                               data-testid="meas-date" /></div>
                    <div><Label className="text-xs text-sand/70">Length unit</Label>
                        <Select value={f.unit_length} onValueChange={(v) => setF({ ...f, unit_length: v })}>
                            <SelectTrigger data-testid="meas-unit-length"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="in">in</SelectItem></SelectContent>
                        </Select></div>
                    <div><Label className="text-xs text-sand/70">Weight unit</Label>
                        <Select value={f.unit_weight} onValueChange={(v) => setF({ ...f, unit_weight: v })}>
                            <SelectTrigger data-testid="meas-unit-weight"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="lbs">lbs</SelectItem></SelectContent>
                        </Select></div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3">
                    {MEAS_FIELDS.map(([k, label]) => (
                        <div key={k}>
                            <Label className="text-[10px] text-sand/70">{label}</Label>
                            <Input type="number" step="0.1" value={f[k] ?? ""}
                                   onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? null : parseFloat(e.target.value) })}
                                   data-testid={`meas-${k}`} />
                        </div>
                    ))}
                </div>
                <div className="mt-3">
                    <Label className="text-xs text-sand/70">Notes</Label>
                    <Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
                </div>
                <Button onClick={save} className="btn-gold w-full mt-3" data-testid="meas-save">
                    <Ruler className="w-4 h-4 mr-1" /> Save log
                </Button>
            </div>
            {rows.length > 0 && (
                <div>
                    <div className="font-mono-label mb-2">Progress ({rows.length} logs)</div>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow>
                                <TableHead>Date</TableHead>
                                {MEAS_FIELDS.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}
                                <TableHead></TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={r.id} data-testid={`meas-row-${r.id}`}>
                                        <TableCell className="text-sand text-xs">{r.date}<br />
                                            <span className="text-[10px]">{r.unit_length}/{r.unit_weight}</span>
                                        </TableCell>
                                        {MEAS_FIELDS.map(([k]) => (
                                            <TableCell key={k} className="text-ivory text-xs">
                                                {r[k] ?? "—"}{delta(k, i)}
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <button onClick={() => del(r.id)} className="text-crimson text-xs"
                                                    data-testid={`meas-del-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};
